import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Droplets, Flame, Star } from "lucide-react";
import { listExperienceReports } from "@/lib/forum-extra.functions";
import { ExperienceReportForm } from "@/components/forum/ExperienceReportForm";

const TITLE = "Fukt- & gasolrapporter per modell | Erfarenhetsdatabas";
const DESC =
  "Öppen erfarenhetsdatabas från svenska husbils- och husvagnsägare: fukttest, gasoltest och helhetsbetyg per märke och modell. Rapportera din egen och hjälp nästa köpare.";
const URL = "https://www.akhusbilfritid.se/erfarenheter";

export const Route = createFileRoute("/erfarenheter")({
  loader: () => listExperienceReports(),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC.slice(0, 158) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC.slice(0, 158) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "Fukt- och gasolrapporter för husbilar och husvagnar",
          description: DESC,
          url: URL,
          creator: { "@type": "Organization", name: "AK Husbil & Fritid" },
          license: "https://www.akhusbilfritid.se/erfarenheter",
          keywords: ["fukttest husbil", "gasoltest husvagn", "husbil erfarenheter", "husvagn fuktskada"],
        }),
      },
    ],
  }),
  component: ExperiencePage,
});

const MOISTURE_LABEL: Record<string, string> = {
  torr: "Torr",
  fuktindikation: "Fuktindikation",
  fuktskada: "Fuktskada",
  ej_testad: "Ej testad",
};
const GAS_LABEL: Record<string, string> = {
  godkand: "Godkänd",
  anmarkning: "Anmärkning",
  underkand: "Underkänd",
  ej_testad: "Ej testad",
};

function ExperiencePage() {
  const initial = Route.useLoaderData();
  const fn = useServerFn(listExperienceReports);
  const { data } = useQuery({
    queryKey: ["experience", "reports"],
    queryFn: () => fn(),
    initialData: initial,
  });

  const summary = (data?.summary ?? []) as Array<{
    key: string;
    brand: string;
    model: string | null;
    count: number;
    avg_rating: number | null;
    dry_share: number | null;
    gas_ok_share: number | null;
  }>;
  const reports = (data?.reports ?? []) as Array<{
    id: string;
    brand: string;
    model: string | null;
    model_year: number | null;
    vehicle_type: string;
    moisture_result: string | null;
    moisture_percent: number | null;
    gas_test_result: string | null;
    overall_rating: number | null;
    body: string | null;
    created_at: string;
    author_name: string | null;
  }>;

  return (
    <section className="bg-warm">
      <div className="container-edge py-10 md:py-16">
        <p className="eyebrow">Fritidsforum · Erfarenhetsdatabas</p>
        <h1 className="mt-3 max-w-3xl font-display text-4xl leading-tight md:text-5xl lg:text-6xl">
          Fukt & gasol – vad säger ägarna, modell för modell?
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Ingen broschyr berättar hur en husbil mår efter tio vintrar. Här samlar communityn riktiga
          fukttest, gasoltest och betyg från egna fordon. Alla siffror kommer från medlemmar — vi lägger
          inget till och tar inget bort.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          <Stat icon={Droplets} label="Rapporter totalt" value={String(reports.length)} />
          <Stat icon={Star} label="Modeller med data" value={String(summary.length)} />
          <Stat
            icon={Flame}
            label="Godkända gasoltest"
            value={`${
              reports.filter((r) => r.gas_test_result === "godkand").length
            } st`}
          />
        </div>

        {summary.length > 0 && (
          <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <caption className="sr-only">Sammanställning av fukt- och gasolrapporter per modell</caption>
                <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-4 py-3">Märke & modell</th>
                    <th scope="col" className="px-4 py-3">Rapporter</th>
                    <th scope="col" className="px-4 py-3">Andel torra</th>
                    <th scope="col" className="px-4 py-3">Gasol godkänd</th>
                    <th scope="col" className="px-4 py-3">Betyg</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((s) => (
                    <tr key={s.key} className="border-t border-border">
                      <th scope="row" className="px-4 py-3 text-left font-semibold">
                        {s.brand} {s.model ?? ""}
                      </th>
                      <td className="px-4 py-3">{s.count}</td>
                      <td className="px-4 py-3">{s.dry_share === null ? "–" : `${s.dry_share} %`}</td>
                      <td className="px-4 py-3">{s.gas_ok_share === null ? "–" : `${s.gas_ok_share} %`}</td>
                      <td className="px-4 py-3">{s.avg_rating === null ? "–" : `${s.avg_rating} / 5`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
          <div className="min-w-0">
            <h2 className="font-display text-3xl">Senaste rapporterna</h2>
            <ul className="mt-5 space-y-3">
              {reports.slice(0, 30).map((r) => (
                <li key={r.id} className="rounded-2xl border border-border bg-card p-5">
                  <p className="font-display text-lg">
                    {r.brand} {r.model ?? ""} {r.model_year ? `(${r.model_year})` : ""}
                  </p>
                  <p className="mt-2 flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wide">
                    <span className="rounded-full bg-secondary px-2.5 py-1 text-muted-foreground">
                      {r.vehicle_type === "husvagn" ? "Husvagn" : "Husbil"}
                    </span>
                    {r.moisture_result && (
                      <span className="rounded-full bg-secondary px-2.5 py-1 text-muted-foreground">
                        Fukt: {MOISTURE_LABEL[r.moisture_result] ?? r.moisture_result}
                        {r.moisture_percent ? ` · ${r.moisture_percent} %` : ""}
                      </span>
                    )}
                    {r.gas_test_result && (
                      <span className="rounded-full bg-secondary px-2.5 py-1 text-muted-foreground">
                        Gasol: {GAS_LABEL[r.gas_test_result] ?? r.gas_test_result}
                      </span>
                    )}
                    {r.overall_rating && (
                      <span className="rounded-full bg-accent/10 px-2.5 py-1 text-accent">
                        {r.overall_rating}/5
                      </span>
                    )}
                  </p>
                  {r.body && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.body}</p>}
                  <p className="mt-3 text-xs text-muted-foreground">
                    {r.author_name || "Medlem"} · {new Date(r.created_at).toLocaleDateString("sv-SE")}
                  </p>
                </li>
              ))}
              {!reports.length && (
                <li className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  Databasen är helt ny — bli den första att rapportera din husbil eller husvagn.
                </li>
              )}
            </ul>
          </div>

          <aside className="space-y-5">
            <ExperienceReportForm />
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="eyebrow">Diskutera vidare</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Har du en fuktindikation du inte förstår? Lägg upp den i forumet — där finns ägare som
                mätt samma modell.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/forum/tagg/$slug"
                  params={{ slug: "fukt" }}
                  className="inline-flex rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
                >
                  #Fukt
                </Link>
                <Link
                  to="/forum/tagg/$slug"
                  params={{ slug: "gasol" }}
                  className="inline-flex rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"
                >
                  #Gasol
                </Link>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="eyebrow">Vi testar varje fordon</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Alla fordon vi säljer är fukttestade och gasoltestade vid leverans.
              </p>
              <Link
                to="/fordon"
                className="mt-3 inline-flex rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                Se vårt lager
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Droplets;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <Icon size={18} className="text-accent" />
      <p className="mt-3 font-display text-2xl">{value}</p>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}
